import Keycloak from 'keycloak-js';
import { API_CLIENT_ID, getConfig } from '@/lib/config';
import { setTokenProvider } from '@/lib/api-client';

/**
 * Keycloak against the shared HazcheckHub realm.
 *
 * Mirrors HazcheckServiceDesk's `src/auth/keycloak.ts`, which matters more than it sounds: a user
 * signs into ServiceDesk and Campaigns in the same browser against the same realm, and two
 * applications initialising the same realm with different options is how one silently signs the
 * other out.
 *
 *   - `onLoad: 'check-sso'` reuses an existing Hub session when someone opens the launcher tile.
 *     Without a session, Keycloak returns to our sign-in page without prompting for credentials.
 *   - `pkceMethod: 'S256'` — authorization-code with PKCE. Never the implicit flow, and a public
 *     client never carries a secret.
 *   - `checkLoginIframe: false` — the iframe check is blocked by third-party-cookie policy in
 *     current browsers and produces spurious logouts when it fails.
 */

let keycloak: Keycloak | null = null;

/**
 * The in-flight initialisation, memoised.
 *
 * THIS IS LOAD-BEARING, and its absence caused a sign-in loop.
 *
 * A `if (keycloak !== null) return` guard is checked before the `await` and assigned after it, so
 * two concurrent callers both run `init()`. React StrictMode double-invokes effects in development,
 * which guarantees exactly two — and an OAuth authorization code is SINGLE USE. The first exchange
 * succeeds; the second gets invalid_grant and resolves unauthenticated; the second result is the one
 * that reaches the UI, so the user is bounced straight back to the sign-in screen having just
 * signed in successfully.
 *
 * Memoising the PROMISE rather than the result makes this idempotent however many times it is
 * called, which matters beyond StrictMode — any double-mount would do the same thing in production.
 */
let initialisation: Promise<Session> | null = null;

/** Roles on `campaigns-api`. Kept in sync with `src/Common/Roles.cs`. */
export const Roles = {
  Admin: 'CampaignsAdmin',
  Author: 'CampaignsAuthor',
  Approver: 'CampaignsApprover',
  Sender: 'CampaignsSender',
  Analyst: 'CampaignsAnalyst',
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

export type Session = {
  isAuthenticated: boolean;
  subject: string | null;
  name: string | null;
  email: string | null;
  roles: readonly string[];
};

export const anonymousSession: Session = {
  isAuthenticated: false,
  subject: null,
  name: null,
  email: null,
  roles: [],
};

/**
 * Initialises Keycloak once and wires the API client's token provider to it.
 *
 * Returns the resolved session rather than a boolean, so the caller does not have to reach back
 * into the module for the claims it just parsed.
 */
export function initialiseAuth(): Promise<Session> {
  // Nullish assignment, so concurrent callers share one initialisation rather than racing.
  initialisation ??= runInitialisation();

  return initialisation;
}

async function runInitialisation(): Promise<Session> {
  const config = getConfig();

  const instance = new Keycloak({
    url: config.auth.url,
    realm: config.auth.realm,
    clientId: config.auth.clientId,
  });

  // `updateToken(70)` refreshes when the token has under 70 seconds left, which has to be
  // comfortably more than the API's 30-second clock skew allowance or a request can arrive with a
  // token the API considers expired.
  instance.onTokenExpired = () => {
    void instance.updateToken(70).catch(() => {
      // A refresh failure means the SSO session is genuinely gone. Reloading sends the user
      // through check-sso again and lands them on the sign-in page with a reason, rather than
      // leaving the app running with a dead token and every request 401ing.
      window.location.reload();
    });
  };

  // A top-level check, matching ServiceDesk's local SSO flow. No silent iframe: Campaigns keeps
  // frame-src restricted to self for the email preview, and third-party cookie blocking must not
  // leave startup waiting for an iframe. Keycloak preserves the current deep link on its round trip.
  await instance.init({
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    checkLoginIframe: false,
    responseMode: 'query',
  });

  keycloak = instance;

  // The API client asks for a token per request and refreshes if it is close to expiry, so a tab
  // left open over lunch does not send one stale request before recovering.
  setTokenProvider(async () => {
    if (keycloak?.authenticated !== true) return undefined;

    try {
      await keycloak.updateToken(70);
    } catch {
      return undefined;
    }

    return keycloak.token;
  });

  return readSession();
}

/**
 * Reads the session from the parsed token.
 *
 * Roles come from `resource_access[campaigns-api].roles`, matching how the API flattens them.
 * Note these are a UX affordance ONLY — the API authorises every request server-side, and a
 * frontend that hides a button has hidden a button.
 */
export function readSession(): Session {
  if (keycloak?.authenticated !== true) return anonymousSession;

  const token = keycloak.tokenParsed;

  const resourceAccess = token?.resource_access as
    | Record<string, { roles?: string[] } | undefined>
    | undefined;

  const roles = (resourceAccess?.[API_CLIENT_ID]?.roles ?? []).filter((role) =>
    Object.values(Roles).includes(role as Role),
  );

  const given = typeof token?.given_name === 'string' ? token.given_name : '';
  const family = typeof token?.family_name === 'string' ? token.family_name : '';
  const fullName = typeof token?.name === 'string' ? token.name : `${given} ${family}`.trim();

  return {
    isAuthenticated: true,
    subject: keycloak.subject ?? null,
    name: fullName.length > 0 ? fullName : null,
    email: typeof token?.email === 'string' ? token.email : null,
    roles,
  };
}

/**
 * Starts sign-in, returning to `redirectPath` afterwards.
 *
 * The path is validated as same-origin-relative before use: a `returnUrl` taken from the address
 * bar and handed to Keycloak unchecked is an open redirect, and the variants that get through a
 * naive check are `//evil.com` and `/\evil.com`.
 */
export async function signIn(redirectPath?: string): Promise<void> {
  if (keycloak === null) throw new Error('signIn() was called before initialiseAuth().');

  await keycloak.login({ redirectUri: window.location.origin + safeRedirectPath(redirectPath) });
}

export async function signOut(): Promise<void> {
  if (keycloak === null) return;

  await keycloak.logout({ redirectUri: window.location.origin });
}

/** Rejects anything that is not a plain same-origin path. */
export function safeRedirectPath(path: string | undefined): string {
  if (path === undefined || path.length === 0) return '/campaigns';

  // Must start with exactly one slash. `//host` is protocol-relative and `/\host` is treated as
  // protocol-relative by some browsers.
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/campaigns';

  // A path that decodes differently than it arrived is trying to smuggle something past this check.
  try {
    if (decodeURIComponent(path) !== path && decodeURIComponent(path).includes('//')) {
      return '/campaigns';
    }
  } catch {
    return '/campaigns';
  }

  return path;
}
